import { Box, SelectChangeEvent } from '@mui/material';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  filtersFormStyle,
  filtersHeaderStyle, filtersResultsStyle, filtersResultsValueStyle, filtersSectionStyle,
  filtersTitleStyle, sortSelectStyle, sortSelectWrapperStyle,
} from '../CustomerInitiativeList/CustomerInitiativeList.style';
import useCustomerInitiativeList from './CiTemplateList.hook';
import { AlertMessageType } from 'basics/enums/maya.enums';
import { SelectOption } from 'basics/options/options.types';
import AlertBanner from 'components/AlertBanner/AlertBanner';
import { FormLayoutContainer } from 'components/FormLayout';
import GridList from 'components/GridList/GridList';
import MayaLayout from 'components/MayaLayout';
import ConfirmationDialog from 'yoda-ui/components/Dialogs/ConfirmationDialog';
import Select from 'yoda-ui/components/Form/Select';
import TextYoda from 'yoda-ui/components/Form/TextYoda';
import Loader from 'yoda-ui/components/Loader';
import MenuItem from 'yoda-ui/components/MenuItem';
import { useYodaCreateForm, YodaFormProvider } from 'yoda-ui/yodaForm';
import { YodaColors, YodaFontSize } from 'yoda-ui/yodaTheme';

const CiTemplateList: FC = () => {
  const { providerFields } = useYodaCreateForm();
  const {
    customerInitiativeListColumnConfig,
    ciTitleFieldConfig,
    newElementButton,
    handleChangeCiTitle,
    cis,
    open,
    setOpen,
    deleteCiCallback,
    rowId,
    loading,
    onPageChange,
    onPageSizeChange,
    rowCountState,
    removeCiLoading,
    ciTemplateSortFieldConfig,
    handleSortChange,
    ciTemplateSortFilter,
  } = useCustomerInitiativeList();
  const { t } = useTranslation();
  const menuCiTemplateLabel = t('maya_main_menu_ci_tpl_label');

  return (
    <MayaLayout>
      <AlertBanner type={ AlertMessageType.VeevaFatalErrorAlert } />
      <GridList
        getRowId={ (row) => row._id }
        loading={ loading }
        title={ menuCiTemplateLabel }
        columnConfig={ customerInitiativeListColumnConfig }
        rows={ cis }
        onPageChange={ onPageChange }
        onPageSizeChange={ onPageSizeChange }
        newElementButton={ newElementButton }
        rowCountState={ rowCountState }
        disableColumnMenu={ true }
        customFilters={
          (
            <YodaFormProvider { ...providerFields }>
              <Box sx={ filtersSectionStyle }>
                <Box sx={ filtersHeaderStyle }>
                  <Box sx={ filtersTitleStyle }>{ t('maya_ci_filter_title') }</Box>
                  {
                    !loading && (
                      <Box sx={ filtersResultsStyle }>
                        { t('maya_ci_filter_results_title') }<Box
                          component="span"
                          sx={ filtersResultsValueStyle }>
                          { t('maya_ci_filter_results_value', { shownItems: cis.length, allItems: rowCountState }) }
                        </Box>
                      </Box>
                    )
                  }
                </Box>

                <FormLayoutContainer sx={ filtersFormStyle }>
                  <TextYoda
                    onChange={ handleChangeCiTitle }
                    { ...ciTitleFieldConfig } />
                </FormLayoutContainer>
              </Box>
              <Box sx={ sortSelectWrapperStyle }>
                <Select
                  value={ ciTemplateSortFilter }
                  onChange={
                    (event: SelectChangeEvent<unknown>, _child: ReactNode) => {
                      const selectedOption = ciTemplateSortFieldConfig.options.find((opt) => opt.id === event.target.value);
                      if (selectedOption) {
                        handleSortChange(selectedOption);
                      }
                    }
                  }
                  sx={ sortSelectStyle }
                >
                  {
                    ciTemplateSortFieldConfig.options.map(
                      (option: SelectOption) => {
                        return <MenuItem key={ option.id }
                          value={ option.id } sx={ { fontSize: YodaFontSize.small } }>{ option.label }</MenuItem>;
                      },
                    )
                  }
                </Select>
              </Box>
            </YodaFormProvider>
          )
        }
      />
      {
        open && (
          <ConfirmationDialog
            dialogTitle={ t('maya_ci_modal_remove') }
            onConfirm={
              () => {
                deleteCiCallback(rowId);
              }
            }
            onConfirmLabel={ removeCiLoading ? <Loader center size={ 1 } color={ YodaColors.inherit } /> : t('actions_confirm_label') }
            onDismiss={ () => setOpen(false) }
            onDismissLabel={ t('actions_cancel_label') }
          >
          </ConfirmationDialog>
        )
      }
    </MayaLayout>
  );
};

export default CiTemplateList;
